import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import media from "../../../media";
import { GlobalStateContext } from "../../../utils/GlobalStateContext";
import { menuConfig, sections } from "../../../utils/Constants";
import { useSelector } from "react-redux";
import { createAccessMap, hasPermission } from "../../../utils/permissions";
import Hamburger from "../../atoms/Hamburger";
const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isActive, setIsActive] = useState("dashboard");
  const rawAccess = useSelector((state) => state.auth.access);

  const currentAuditType =
    location.state?.auditType || secureLocalStorage.getItem("auditType");

  const segments = location.pathname.split("/").filter(Boolean);
  const base = segments[0];
  const { globalState, setGlobalState } = useContext(GlobalStateContext);

  const role = secureLocalStorage.getItem("role");
  const [isFeedbackRequestsActive, setIsFeedbackRequestsActive] =
    useState(false);

  const [isEscalationsActive, setIsEscalationsActive] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(null);

  /* =======================================================
     STEP 2: BUILD ACCESS MAP (WITH FALLBACK SUPPORT)
     ======================================================= */
  const accessMap = useMemo(() => {
    return createAccessMap(rawAccess);
  }, [rawAccess]);

  /* =======================================================
     HELPER: CHECK ACCESS
     ======================================================= */
  const hasSidebarAccess = (key) => {
    return accessMap?.[key]?.sidebarview;
  };

  /*=======================================================
    GLOBAL TABLE SELECTION RESET (SINGLE SOURCE OF TRUTH)
    =======================================================*/
  useEffect(() => {
    if (
      globalState?.selectedRowKeys?.length ||
      globalState?.selectedRowKeysChildInfo?.length
    ) {
      setGlobalState((prev) => ({
        ...prev,
        selectedRowKeys: [],
        selectedRowKeysChildInfo: [],
      }));
    }

    //CLEAR SEARCH WHEN NAVIGATION CHANGES
    const searchInput = document.querySelector(".inputS");
    if (searchInput) searchInput.value = "";

    secureLocalStorage.removeItem("hasSearchedValue");
    secureLocalStorage.removeItem("searchKeyword");
  }, [location.pathname]);

  const child = segments[1];

  const handleFeedbackRequestsClick = () => {
    setIsFeedbackRequestsActive((prev) => !prev); // toggle open/close
  };

  const handleClientEscalationClick = () => {
    setIsEscalationsActive((prev) => !prev); // TOGGLE
  };

  const handleClick = (val) => {
    setIsActive((prev) => (prev === val ? "" : val));
  };

  const handleSubMenuClick = (val, path, type) => {
    const cleanPath = path?.replace("/", "");
    if (type) {
      secureLocalStorage.setItem("auditType", type);
    }
    setGlobalState((prev) => ({
      ...prev,
      resetSearch: Date.now(),
    }));
    const lastPageSearchIn = secureLocalStorage.getItem("lastPageSearchIn");
    if (cleanPath && lastPageSearchIn && cleanPath !== lastPageSearchIn) {
      const searchInput = document.querySelector(".inputS");
      if (searchInput) searchInput.value = "";

      secureLocalStorage.removeItem("hasSearchedValue");
      secureLocalStorage.removeItem("searchKeyword");
    }
  };

  useEffect(() => {
    // RESET submenu states first
    setIsFeedbackRequestsActive(false);
    setIsEscalationsActive(false);

    // DASHBOARD
    if (!base || base === "dashboard") {
      setIsActive("dashboard");
      return;
    }

    // QMS (list + detail pages)
    if (
      [
        "auditForms",
        "audits",
        "deleted-audits",
        "processes",
        "clients",
        "departments",
        "escalations",
      ].includes(base)
    ) {
      setIsActive("qms");

      if (base === "escalations") {
        setIsEscalationsActive(true);
        setActiveSubMenu("Escalations");
      }
      if (base === "audits") {
        setActiveSubMenu("Audits");
      }

      return;
    }

    // VOC
    if (["feedback-requests", "forms"].includes(base)) {
      setIsActive("voc");

      if (base === "feedback-requests") {
        setIsFeedbackRequestsActive(true);
        setActiveSubMenu("Feedback Requests");
      }
      return;
    }

    // Access Control
    if (["users", "roles", "permissions", "sections"].includes(base)) {
      setIsActive("mpsru");
      return;
    }

    // SETTINGS
    if (base === "settings") {
      setIsActive("settings");
    }
  }, [base]);

  const renderSubLinks = (subLinks) =>
    subLinks?.map(({ path, label, type }) => {
      const isActive =
        location.pathname.startsWith(path) &&
        (type ? currentAuditType === type : true);

      return (
        <Link
          key={label}
          to={path}
          state={{ auditType: type }}
          onClick={() => {
            secureLocalStorage.setItem("auditType", type);

            handleSubMenuClick(null, path);

            // CRITICAL FIX
            if (path.startsWith("/escalations")) {
              setIsActive("qms");
              setIsEscalationsActive(true);
              setActiveSubMenu("Escalations");
            }

            if (path.startsWith("/feedback-requests")) {
              setIsActive("voc");
              setIsFeedbackRequestsActive(true);
            }
          }}
          className={`links ${isActive ? "subMenu-active-item" : ""}`}
        >
          <span className="dashboardstyle">{label}</span>
        </Link>
      );
    });

  return (
    <div className="sidenav">
      {/* LOGO */}
      <div
        className="logo-area"
        onClick={() => {
          navigate("/dashboard");
        }}
      >
        <div className="logoDiv">
           {globalState?.logo && (
         <img
            src={process.env.REACT_APP_BACKEND + `/images/${globalState?.logo}`}
            className="logo-img"
            alt="logo"
          />
        )}
        <span className="qcc_logo">{globalState?.logoTitle}</span>  
        </div> 
        <Hamburger loc={location} />
      </div>
      <div className="menu">
        {/*Dashboard*/}
        <div
          className={
            location.pathname === "/dashboard" || location.pathname === "/"
              ? "menuActive"
              : ""
          }
        >
          <span
            className="d-flex links"
            onClick={() => {
              navigate("/dashboard");
            }}
          >
            <span className="dboardLnkContnt link-content">
              <img src={media.dashboardIcon.svg} alt="icon" />
              Dashboard
              {/* {JSON.stringify(sections, null, 2)} */}
            </span>
            <i className={isActive === "dashboard" ? "" : ""} />
          </span>
        </div>

        {sections?.map(({ key, title, icon, roles, path, isVisible }) => {
          if (!roles.includes(role)) return null;

          if (isVisible && !isVisible()) return null;

          // SPECIAL CASE FOR DIRECT LINK MENU
          if (key === "switchRole") {
            const links = menuConfig?.[key]?.[0]?.[role];

            if (!links || !links.length) return null;

            const link = links[0];

            return (
              <div
                key={key}
                className={
                  location.pathname.startsWith(link?.path) ? "menuActive" : ""
                }
              >
                <Link
                  to={link.path}
                  className="d-flex links"
                  onClick={() => {
                    handleClick(link?.path?.replace("/", ""));
                    handleSubMenuClick(link?.path?.replace("/", ""));
                  }}
                >
                  <span className="link-content">
                    <img style={{ marginRight: 0 }} src={icon} alt="icon" />
                    {link.label}
                  </span>
                </Link>
              </div>
            );
          }

          const links = menuConfig?.[key]?.[0]?.[role];

          // FILTER BASED ON ACCESS
          const filteredLinks = links
            ?.map((item) => {
              if (item.subLinks) {
                const filteredSubLinks = item.subLinks.filter((sub) => {
                  return hasPermission(accessMap, sub.key, "sidebarview");
                });

                if (filteredSubLinks?.length > 0) {
                  return { ...item, subLinks: filteredSubLinks };
                }
                return null;
              }

              return hasPermission(accessMap, item.key, "sidebarview")
                ? item
                : null;
            })
            .filter(Boolean);

          // HIDE EMPTY SECTIONS
          if (!filteredLinks?.length) return null;

          return (
            <div key={key} className={isActive === key ? "menuActive" : ""}>
              <span className="d-flex links" onClick={() => handleClick(key)}>
                <span className="link-content">
                  <img src={icon} alt="icon" /> {title}
                </span>

                <i
                  className={
                    isActive === key
                      ? "fa fa-angle-up dropdown"
                      : "fa fa-angle-down dropdown"
                  }
                />
              </span>

              <div
                className={
                  isActive === key ? "activeClass" : "inactiveClass dropwdown"
                }
              >
                {filteredLinks?.map(({ path, label, type, subLinks }) => (
                  <React.Fragment key={label}>
                    {subLinks ? (
                      <>
                        <span
                          className="links subMnu"
                          onClick={() =>
                            setActiveSubMenu((prev) =>
                              prev === label ? null : label,
                            )
                          }
                        >
                          <span className="dashboardstyle">
                            {label}
                            <i
                              className={`fa dropdown ${
                                activeSubMenu === label
                                  ? "fa-angle-up"
                                  : "fa-angle-down"
                              }`}
                            />
                          </span>
                        </span>

                        <div
                          className={
                            activeSubMenu === label
                              ? "activeClass subMnuChildActive"
                              : "inactiveClass dropwdown"
                          }
                        >
                          {renderSubLinks(subLinks)}
                        </div>
                      </>
                    ) : (
                      <Link
                        to={path}
                        className={`links ${
                          location.pathname === path ||
                          (path !== "/forms" &&
                            location.pathname.startsWith(path))
                            ? "subMenu-active-item"
                            : ""
                        }`}
                        onClick={() => handleSubMenuClick(key, path, type)}
                      >
                        <span className="dashboardstyle">{label}</span>
                      </Link>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NavBar;
